import {
  createContext,
  useContext,
  Suspense,
  ComponentType,
  ReactNode,
} from 'react'
import {
  SuspendedContextProps,
  ContextSuspenseProps,
  PropsWithFallback,
  ContextProvider,
} from './suspended-context.types'
import SuspendedContextClient from './suspended-context.client'

const withSuspendedContext = <T extends any, U extends (...args: any[]) => any>(
  ContextProvider: ContextProvider<T>,
  useContextProviderValue: U,
) => {
  const LoadingContext = createContext<boolean | null>(true)

  const ContextSuspense = ({ children, fallback }: ContextSuspenseProps) => {
    const isLoading = useContext(LoadingContext)
    return isLoading ? fallback : children
  }

  const SuspendedProvider = ({
    initialValue,
    value,
    children,
    fallback,
  }: SuspendedContextProps<T>) => {
    if (initialValue) {
      return <ContextProvider value={initialValue}>{children}</ContextProvider>
    }

    if (!(value instanceof Promise)) {
      return (
        <LoadingContext.Provider value={false}>
          <ContextProvider value={value as T}>{children}</ContextProvider>
        </LoadingContext.Provider>
      )
    }

    return (
      <Suspense
        fallback={
          <LoadingContext.Provider value={true}>
            {fallback ?? children}
          </LoadingContext.Provider>
        }
      >
        <LoadingContext.Provider value={false}>
          <SuspendedContextClient value={value} provider={ContextProvider}>
            {children}
          </SuspendedContextClient>
        </LoadingContext.Provider>
      </Suspense>
    )
  }

  const useSuspendedContext = (): ReturnType<
    typeof useContextProviderValue
  > => {
    const isLoading = useContext(LoadingContext)
    try {
      const value = useContextProviderValue()
      return value as ReturnType<typeof useContextProviderValue>
    } catch (error) {
      if (!isLoading) {
        throw error
      }
      throw new Error(
        'You are trying to access a suspended context while it is loading. Make sure to wrap the component with a ContextSuspense or allow the context to be null/undefined.',
      )
    }
  }

  const withSuspense = <P extends object>(
    Component: ComponentType<P>,
    fallback?: ReactNode,
  ) => {
    const SuspendedComponent = (props: PropsWithFallback<P>) => (
      <ContextSuspense fallback={props.fallback ?? fallback}>
        <Component {...props} />
      </ContextSuspense>
    )

    SuspendedComponent.displayName = Component.displayName

    return SuspendedComponent
  }

  return [
    SuspendedProvider,
    useSuspendedContext,
    withSuspense,
    ContextSuspense,
  ] as const
}

export default withSuspendedContext
